<script setup>
import { tv } from 'tailwind-variants';
import { computed, useAttrs, useListeners } from 'vue';
import { useListboxContext } from './useListbox';

/**
 * @typedef {object} ListboxItemProps
 * @property {('default'|'critical')} [variant='default'] - Style variant of the listbox item
 * @property {boolean} [isActive=false] - Whether the item is currently active/selected
 * @property {boolean} [isDisabled=false] - Whether the item is disabled
 * @property {boolean} [isShowCheckmark=false] - Whether to show a checkmark for the item
 * @property {boolean} [isHovered=false] - Whether the item is hovered
 * @property {boolean} [isHighlighted=false] - Whether the item is highlighted
 * @property {string} [contentClass=''] - Additional class for the content slot
 * @property {boolean} [isManualHighlight=false] - Whether to highlight the item manually
 */

/**
 * @type {ListboxItemProps}
 */
const props = defineProps({
  variant: {
    type: String,
    default: 'default',
  },
  isActive: {
    type: Boolean,
    default: false,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  isShowCheckmark: {
    type: Boolean,
    default: false,
  },
  isHovered: {
    type: Boolean,
    default: false,
  },
  isHighlighted: {
    type: Boolean,
    default: false,
  },
  isManualHighlight: {
    type: Boolean,
    default: false,
  },
  contentClass: {
    type: String,
    default: '',
  },
});

const attrs = useAttrs();
const listeners = useListeners();

const activeListeners = computed(() => {
  if (props.isDisabled) {
    const { click, ...rest } = listeners;
    return rest;
  }
  return listeners;
});

const listboxItemRecipe = tv({
  slots: {
    button:
      'flex items-center justify-between w-full text-brand-text rounded-lg',
    content: 'flex items-center gap-2',
    checkmark: 'text-inherit w-3 h-3 flex-shrink-0',
  },
  variants: {
    variant: {
      default: {
        button: [
          'text-brand-text',
          'active:bg-brand-gray-7 dark:active:bg-brand-gray-15',
          'data-[hover=true]:bg-brand-gray-6 dark:data-[hover=true]:bg-brand-gray-14',
          'data-[highlighted=true]:bg-brand-gray-6 dark:data-[highlighted=true]:bg-brand-gray-14',
        ],
      },
      critical: {
        button: [
          'active:bg-brand-red-5 dark:active:bg-brand-red-7',
          'data-[hover=true]:bg-brand-red-4 dark:data-[hover=true]:bg-brand-red-6',
          'data-[highlighted=true]:bg-brand-red-4 dark:data-[highlighted=true]:bg-brand-red-6',
        ],
      },
    },
    isActive: {
      true: {
        button: [
          'font-medium text-brand-text',
          'bg-brand-gray-7 hover:bg-brand-gray-7 active:bg-brand-gray-7',
          'dark:bg-brand-gray-14 dark:hover:bg-brand-gray-14 dark:active:bg-brand-gray-14',
        ],
      },
      false: {
        button: '',
      },
    },
    isDisabled: {
      true: {},
      false: {},
    },
    size: {
      xs: {
        button: 'py-0.5 px-1.5 text-xs',
      },
      sm: {
        button: 'py-1 px-1.5 text-xs',
      },
      default: {
        button: 'py-1.5 px-1.5 text-sm',
      },
    },
    isManualHighlight: {
      true: {
        button: '',
      },
      false: {
        button: '',
      },
    },
  },
  compoundVariants: [
    {
      variant: ['default', 'critical'],
      isDisabled: true,
      class: {
        button:
          'cursor-not-allowed !text-brand-gray-11 hover:bg-transparent !active:bg-transparent hover:!bg-transparent select-none',
      },
    },
    {
      isManualHighlight: false,
      variant: 'default',
      class: {
        button: 'hover:bg-brand-gray-6 dark:hover:bg-brand-gray-14',
      },
    },
    {
      isManualHighlight: false,
      variant: 'critical',
      class: {
        button:
          'text-brand-red-14 dark:text-brand-red-11 hover:bg-brand-red-4 dark:hover:bg-brand-red-6',
      },
    },
  ],
  defaultVariants: {
    isActive: false,
    variant: 'default',
    isDisabled: false,
    isManualHighlight: false,
  },
});

const { rootProps } = useListboxContext();

const listboxItemClasses = computed(() =>
  listboxItemRecipe({
    isActive: props.isActive,
    variant: props.variant,
    size: rootProps.size.value,
    isDisabled: props.isDisabled,
    isShowCheckmark: props.isShowCheckmark,
    isHovered: props.isHovered && !props.isDisabled,
    isManualHighlight: props.isManualHighlight,
  })
);
</script>

<template>
  <div data-component="CsListItem" v-bind="attrs" v-on="activeListeners">
    <div
      role="button"
      :class="listboxItemClasses.button()"
      :data-hover="props.isHovered && !props.isDisabled"
      :data-highlighted="props.isHighlighted"
    >
      <div
        data-slot="content"
        :class="listboxItemClasses.content({ class: props.contentClass })"
      >
        <slot />
      </div>

      <svg
        v-if="props.isShowCheckmark"
        :class="listboxItemClasses.checkmark()"
        viewBox="0 0 17 18"
      >
        <polyline
          fill="none"
          points="1 9 7 14 15 4"
          stroke="currentColor"
          :stroke-dasharray="22"
          :stroke-dashoffset="props.isActive ? 44 : 66"
          stroke-linecap="round"
          stroke-linejoin="round"
          :stroke-width="2"
          :style="{
            transition: props.isActive
              ? 'stroke-dashoffset 200ms linear 0.1s'
              : 'stroke-dashoffset 200ms linear 0.1s',
          }"
        />
      </svg>
    </div>
  </div>
</template>
